/**
 * §A4's two expectation digests — `TOs Expectation Summary` and `UMs Expectation Summary`.
 *
 * **What §A4 asks for, and what is genuinely missing.** Both digests are *"AI-composed"* from
 * the officials' replies to the intake email — §A4's own foot says so: *"composed from the
 * officials' intake replies — items 7 (Past Hockey Experience) and 8 (Expectations Of The
 * Tournament And The TD)"*. What is missing is **the digest**, and only the digest. Nothing
 * here composes one, and nothing invents one either.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * CORRECTION — THIS HEADER USED TO SAY "THIS BUILD HAS NO INTAKE STORE"
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * That was true when it was written and has not been true for some time, and it was the sort
 * of stale claim that sends the next reader looking for a column to add. The store is real:
 *
 *   · `official.past_experience` and `.expectation_of_tournament` (migration 003) hold the
 *     answers to items 7 and 8, and the version 2 bootstrap bundle writes them.
 *   · `intake_submission` (migration 003, keyed `(official_id, raw_ref)` by migration 036)
 *     holds one row per reply — who sent it, its subject, and when it arrived.
 *   · `GET /officials/:tmsPersonId` carries all of it, behind `requireTd`: `pastExperience`,
 *     `expectationOfTournament`, `tdExpectation` and `intakeReceivedAt`.
 *
 * **Where a reply is actually read today.** One official at a time, on their own profile, in
 * §B9's `Past Experience / Expectation` panel — `PastExperiencePanel` in
 * `ProfileDetailPanels.tsx`. The answers in the official's own words, the named absence where
 * no reply arrived, `Replied <date>` off the matched submission, and the TD's own note beside
 * them. Anyone here to make §A4 real should start from that panel's data, not from a schema.
 *
 * What this panel still cannot do is the ACROSS-the-panel reading: ranking the threads that
 * recur in twenty-two replies, and grouping them under §A4's themes. That is a composition
 * step nothing in this build performs. So the panel keeps its §A1 position, its §A4 title,
 * note and foot, and says plainly that the digest has not been composed. The alternatives were
 * both worse: a fabricated digest would put invented sentences about real officials in front
 * of the TD, and omitting the panel would leave §A1's layout silently a third short with
 * nothing naming the gap.
 *
 * **The right-hand note is not a count.** §A4 prints `16 Of 22 Replies Recorded`, a figure
 * read off the replies this panel is given. It is given none — the digest has no props and
 * makes no fetch — and `0 Of 22` would be a claim that twenty-two officials were asked and
 * none replied, which is a finding rather than a blank. The note therefore states the state of
 * the digest instead.
 */

import { EmptyState } from "../../app/states.js";

export interface ExpectationSummaryPanelProps {
  /** `to` / `um` — the panel's heading id, so the two cannot collide. */
  id: string;
  /** `TOs Expectation Summary` / `UMs Expectation Summary`, verbatim from §A4. */
  title: string;
  /** §A4's left note. */
  note: string;
  /** §A4's group-1 kicker — the ranked-threads heading this digest will carry. */
  threadsKicker: string;
  /** §A4's observed group-2 headings, in the order the spec lists them. */
  themeHeadings: readonly string[];
  /** §A4's foot, verbatim. */
  foot: React.ReactNode;
}

export function ExpectationSummaryPanel({
  id,
  title,
  note,
  threadsKicker,
  themeHeadings,
  foot,
}: ExpectationSummaryPanelProps): React.JSX.Element {
  return (
    <section
      className="panel panel--d2"
      aria-labelledby={`${id}ExpectTitle`}
      style={{ marginTop: 18 }}
      data-testid={`${id}Expect`}
    >
      <div className="panel__head">
        <h3 className="panel__title" id={`${id}ExpectTitle`}>
          {title}
        </h3>
        <span className="panel__note">{note}</span>
        <span className="spacer" />
        <span className="panel__note">Digest Not Yet Composed</span>
      </div>

      <EmptyState
        title="No Digest Has Been Composed Yet"
        detail={`The replies themselves are stored: each official's answers to items 7 and 8 are on their own profile, in the Past Experience / Expectation panel. What does not exist is this reading ACROSS them — nothing in this build ranks the threads that recur in twenty-odd replies. When it lands it will carry ${threadsKicker} as its ranked list, and themed blocks for ${themeHeadings.join(", ")}. Nothing is summarised here in the meantime, because an invented thread would put sentences the panel never wrote in front of the Technical Delegate.`}
      />

      {/*
       * What the Technical Delegate can actually do about it, which since the replies landed is
       * something rather than nothing. An empty panel that only names its own gap leaves him
       * with a screen and no next step; this points him at the place the same material can be
       * read today, in one line, without pretending a control exists here to compose it.
       */}
      <div className="panel__foot" data-testid={`${id}ExpectWhatToDo`}>
        <b>What to do —</b> read them one at a time: each official&rsquo;s reply is on their own
        profile, under <b>Past Experience / Expectation</b>. No digest across the panel is
        composed in this build.
      </div>

      <div className="panel__foot">{foot}</div>
    </section>
  );
}
